// component/screens/setting_tabs/jira/JiraCallbackHandler.tsx
import React, { useEffect, useState, useRef } from 'react';
import { JiraIntegrationService } from '../../../../services/JiraIntegrationService';

/**
 * This component handles the Jira OAuth callback
 * It should be rendered at /jira/callback route
 */
const JiraCallbackHandler: React.FC = () => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Completing Jira Connection...');
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (!hasProcessed.current) {
      hasProcessed.current = true;
      handleCallback();
    }
  }, []);

  const closePopup = () => {
    console.log('🚪 Attempting to close popup window...');
    
    // Notify parent window first
    if (window.opener && !window.opener.closed) {
      try {
        const allowedOrigins = [
          'https://app.feebdack.in',
          'https://testapp.feebdack.in',
          window.location.origin,
        ];
        const targetOrigin = allowedOrigins.includes(document.referrer
          ? new URL(document.referrer).origin
          : '') ? new URL(document.referrer).origin : window.location.origin;
        window.opener.postMessage({ type: 'JIRA_OAUTH_COMPLETE', success: true }, targetOrigin);
      } catch (e) {
        console.warn('Could not notify parent window:', e);
      }
    }
    
    // Immediately try to close
    try {
      window.close();
      console.log('✅ Window.close() called');
    } catch (e) {
      console.error('❌ Error calling window.close():', e);
    }
    
    // Fallback: if window.close() doesn't work, show a message and try alternative
    setTimeout(() => {
      if (window.opener && !window.opener.closed) {
        console.log('⚠️ Window did not close, trying alternative method...');
        setMessage('Please close this window manually.');
        
        // Try closing again
        try {
          window.close();
        } catch (e) {
          console.error('❌ Second close attempt failed:', e);
        }
      }
    }, 1000);
  };

  const handleCallback = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const success = urlParams.get('success');
      const error = urlParams.get('error');
      const code = urlParams.get('code');
      const state = urlParams.get('state');

      console.log('🔄 Jira OAuth callback received:', { 
        success, 
        error, 
        hasCode: !!code, 
        hasState: !!state,
        fullUrl: window.location.href 
      });

      // Case 1: Backend already processed and redirected with success/error
      if (success === 'true') {
        console.log('✅ Jira OAuth successful (backend confirmed)!');
        setStatus('success');
        setMessage('Jira connected successfully!');
        JiraIntegrationService.handleOAuthCallback(true);
        
        console.log('⏱️ Waiting 1.5s before closing popup...');
        // Wait a bit to show success, then close
        setTimeout(() => {
          console.log('⏰ Timeout complete, calling closePopup()...');
          closePopup();
        }, 1500);
        return;
      }

      if (error) {
        console.error('Jira OAuth error:', decodeURIComponent(error));
        setStatus('error');
        setMessage('Jira connection failed. Please try again.');
        JiraIntegrationService.handleOAuthCallback(false);
        
        // Keep error visible longer
        setTimeout(() => {
          closePopup();
        }, 3000);
        return;
      }

      // Case 2: We have OAuth code - backend is processing
      if (code && state) {
        console.log('📝 OAuth code received, waiting for backend processing...');
        setMessage('Verifying connection with Jira...');
        
        // Poll backend status to check if connection is complete
        let pollAttempts = 0;
        const maxPollAttempts = 30; // 30 attempts * 1 second = 30 seconds max
        
        const pollStatus = async (): Promise<boolean> => {
          try {
            console.log(`🔍 Polling backend status (attempt ${pollAttempts + 1}/${maxPollAttempts})...`);
            const status = await JiraIntegrationService.checkIntegrationStatus();
            
            console.log('📊 Backend status:', status);
            
            if (status.isConnected) {
              console.log('✅ Backend confirmed Jira is connected via status API');
              setStatus('success');
              setMessage('Jira connected successfully!');
              JiraIntegrationService.handleOAuthCallback(true);
              
              console.log('⏱️ Waiting 1.5s before closing popup...');
              // Show success message briefly, then close
              setTimeout(() => {
                console.log('⏰ Timeout complete, calling closePopup()...');
                closePopup();
              }, 1500);
              
              return true;
            }
            
            console.log('⏳ Not connected yet, will poll again...');
            return false;
          } catch (error) {
            console.error('❌ Error polling status:', error);
            return false;
          }
        };
        
        // Start polling
        const pollInterval = setInterval(async () => {
          pollAttempts++;
          
          const isConnected = await pollStatus();
          
          if (isConnected) {
            clearInterval(pollInterval);
            return;
          }
          
          if (pollAttempts >= maxPollAttempts) {
            clearInterval(pollInterval);
            console.warn('⚠️ Backend processing timeout');
            setStatus('error');
            setMessage('Connection verification timed out. Please check your integration settings.');
            JiraIntegrationService.handleOAuthCallback(false);
            
            setTimeout(() => {
              closePopup();
            }, 3000);
          }
        }, 1000); // Poll every 1 second

        return;
      }

      // Case 3: No clear parameters - something went wrong
      console.warn('⚠️ Invalid OAuth callback - missing required parameters');
      setStatus('error');
      setMessage('Invalid callback parameters. Please try connecting again.');
      JiraIntegrationService.handleOAuthCallback(false);
      
      setTimeout(() => {
        closePopup();
      }, 2000);

    } catch (error) {
      console.error('❌ Error handling Jira callback:', error);
      setStatus('error');
      setMessage('Failed to complete connection');
      JiraIntegrationService.handleOAuthCallback(false);
      
      setTimeout(() => {
        closePopup();
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center p-6 max-w-md">
        {status === 'processing' && (
          <>
            <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold mb-2 text-gray-900">
              {message}
            </h2>
            <p className="text-gray-600">
              Please wait while we complete the connection...
            </p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-2 text-gray-900">
              Success!
            </h2>
            <p className="text-gray-600">
              {message}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Closing window...
            </p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold mb-2 text-gray-900">
              Connection Failed
            </h2>
            <p className="text-gray-600 mb-4">
              {message}
            </p>
            <button 
              onClick={() => window.close()}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Close Window
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default JiraCallbackHandler;